import { DeformableTransform } from '~/domain/dvf/deformableTransform';
import { Image3D } from '~/domain/patient/image3d';
import {
    warpImageVolume,
    WarpImageVolumeOptions,
} from '~/domain/services/warpImageVolume';
import { buildDerivedSeriesFromVolume } from '~/domain/services/dicomRewriteImage';

export interface DeformedPreview {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly dicomDatasets: any[];
    readonly seriesInstanceUid: string;
    readonly fieldCoverage: number;
}

export async function buildDeformedPreview(
    fixedImage: Image3D,
    movingImage: Image3D,
    transform: DeformableTransform,
    seriesNumber: number,
    options: WarpImageVolumeOptions = {}
): Promise<DeformedPreview | null> {
    const dstGrid = fixedImage.grid;
    const warped = await warpImageVolume(
        movingImage.pixelData,
        movingImage.grid,
        dstGrid,
        transform,
        options
    );
    if (!warped) {
        return null;
    }

    const total = dstGrid.sizeX * dstGrid.sizeY * dstGrid.sizeZ;
    const fieldCoverage =
        total > 0 ? (total - warped.voxelsOutsideField) / total : 0;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const exampleDataset: any = fixedImage.firstSlice.datasetWrapper.dataset;
    const dicomDatasets = await buildDerivedSeriesFromVolume(
        warped.pixels,
        dstGrid,
        exampleDataset,
        {
            // The warp does not leave the fixed image's coordinate system.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            preserveFrameOfReferenceUid: exampleDataset.FrameOfReferenceUID,
            seriesNumber,
            derivationDescription:
                deformedPreviewDerivationDescription(fieldCoverage),
        }
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const seriesInstanceUid: unknown = dicomDatasets[0]?.SeriesInstanceUID;
    if (typeof seriesInstanceUid !== 'string' || seriesInstanceUid === '') {
        throw new Error(
            'The deformed preview series has no Series Instance UID.'
        );
    }
    return { dicomDatasets, seriesInstanceUid, fieldCoverage };
}

function deformedPreviewDerivationDescription(fieldCoverage: number): string {
    const covered = Math.round(fieldCoverage * 100);
    return (
        'Deformed preview built in the viewer by resampling the moving series ' +
        "through a deformable registration's sampled displacement field onto " +
        'the fixed image geometry. The field is coarser than the transform ' +
        'that produced the registration, so this approximates that transform ' +
        `rather than reproducing it. The field covered ${covered}% of the ` +
        'output; the remainder is background, not anatomy. For review only.'
    );
}
